import { useDrag } from '@use-gesture/react'
import { useSpring, useTransform } from 'motion/react'
import React, { useEffect } from 'react'

import { useHapticFeedback } from '@blade/ui-kit'

import { PullToRefreshLoader } from './pull-to-refresh-loader'

import type { MotionValue } from 'motion/react'

const isElementIgnored = (element: EventTarget) => {
  return !!(
    element instanceof HTMLElement && element.closest('[data-ignore-ptr]')
  )
}

const THRESHOLD = 160
const PULLING_MARGIN = 80

type Args = {
  onPull: () => void
  enabled: boolean
  pulling: boolean
}
type UsePullToRefreshReturn = {
  yMotionValue: MotionValue<number>
  bindDraggable: () => object
  loaderElement: React.ReactElement
}

export const usePullToRefresh = ({
  enabled,
  onPull,
  pulling,
}: Args): UsePullToRefreshReturn => {
  const yValue = useSpring(0, { stiffness: 800, damping: 100 })
  const haptics = useHapticFeedback()

  useEffect(() => {
    if (pulling) {
      yValue.set(PULLING_MARGIN)
    } else {
      yValue.set(0)
    }
  }, [pulling, yValue])

  const bind = useDrag(
    ({ movement: [, movementY], cancel, dragging, event, first }) => {
      const { currentTarget, target } = event

      if (
        !(currentTarget instanceof HTMLElement && target instanceof HTMLElement)
      ) {
        return
      }

      if (isElementIgnored(target)) {
        cancel()
        return
      }

      if (!currentTarget.contains(target)) {
        return
      }

      if (movementY < 0) {
        cancel()
        return
      }

      if (!dragging && !pulling) {
        cancel()
        yValue.set(0)
        return
      }

      if (movementY > THRESHOLD) {
        if (!pulling) {
          haptics.impactOccurred('medium')
          onPull()
          cancel()
        }
        return
      }

      if (first) {
        haptics.impactOccurred('soft')
      }

      yValue.jump(movementY)
    },
    {
      axis: 'y',
      filterTaps: true,
      enabled: !pulling && enabled,
    },
  )

  const loaderOpacity = useTransform(() =>
    Math.min(yValue.get() / PULLING_MARGIN, 1),
  )
  const progress = useTransform(() => yValue.get() / THRESHOLD)

  const loaderEl = (
    <PullToRefreshLoader
      height={PULLING_MARGIN}
      progress={progress}
      opacity={loaderOpacity}
      pulling={pulling}
    />
  )

  return {
    bindDraggable: bind as () => React.DOMAttributes<HTMLDivElement>,
    loaderElement: loaderEl,
    yMotionValue: yValue,
  }
}
